import React, { useState, useEffect, useCallback } from "react";
import {
  Header,
  Segment,
  Table,
  Button,
  Label,
  Icon,
  Pagination,
} from "semantic-ui-react";
import toastr from "toastr";
import ConfirmPopup from "../../components/ConfirmPopup";
import apis from "../../apis";
import AnnouncementFormModal from "./AnnouncementFormModal";

const COLOR_MAP = {
  info: { color: "blue", label: "Info" },
  warning: { color: "yellow", label: "Warning" },
  danger: { color: "red", label: "Danger" },
  success: { color: "green", label: "Success" },
};

const PAGE_LIMIT = 10;
const MAX_MESSAGE_LENGTH = 80;
const TABLE_COLUMN_COUNT = 7;

function truncateMessage(message) {
  if (!message || message.length <= MAX_MESSAGE_LENGTH) return message;
  return `${message.slice(0, MAX_MESSAGE_LENGTH)}...`;
}

function isAnnouncementActive(announcement) {
  return announcement.isActive && new Date(announcement.expiresAt) > new Date();
}

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString();
}

const AnnouncementsPage = () => {
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const loadAnnouncements = useCallback(async (page) => {
    setIsLoading(true);
    try {
      const { data } = await apis.announcements.getAllAnnouncements({
        page,
        limit: PAGE_LIMIT,
      });
      setItems(data.items || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toastr.error("Failed to load announcements");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements(currentPage);
  }, [currentPage, loadAnnouncements]);

  const handlePageChange = useCallback((e, { activePage }) => {
    setCurrentPage(activePage);
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingAnnouncement(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((announcement) => {
    setEditingAnnouncement(announcement);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingAnnouncement(null);
  }, []);

  const handleModalSubmit = useCallback(
    async (formData) => {
      setModalLoading(true);
      try {
        if (editingAnnouncement) {
          await apis.announcements.updateAnnouncement(editingAnnouncement._id, formData);
          toastr.success("Announcement updated");
        } else {
          await apis.announcements.createAnnouncement(formData);
          toastr.success("Announcement created");
        }
        setModalOpen(false);
        setEditingAnnouncement(null);
        loadAnnouncements(currentPage);
      } catch (err) {
        toastr.error(editingAnnouncement ? "Failed to update announcement" : "Failed to create announcement");
      } finally {
        setModalLoading(false);
      }
    },
    [editingAnnouncement, currentPage, loadAnnouncements],
  );

  const handleExpire = useCallback(
    async (id) => {
      setActionLoadingId(id);
      try {
        await apis.announcements.expireAnnouncement(id);
        toastr.success("Announcement expired");
        loadAnnouncements(currentPage);
      } catch (err) {
        toastr.error("Failed to expire announcement");
      } finally {
        setActionLoadingId(null);
      }
    },
    [currentPage, loadAnnouncements],
  );

  const handleDelete = useCallback(
    async (id) => {
      setActionLoadingId(id);
      try {
        await apis.announcements.deleteAnnouncement(id);
        toastr.success("Announcement deleted");
        loadAnnouncements(currentPage);
      } catch (err) {
        toastr.error("Failed to delete announcement");
      } finally {
        setActionLoadingId(null);
      }
    },
    [currentPage, loadAnnouncements],
  );

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">
            Loading...
          </Table.Cell>
        </Table.Row>
      );
    }

    if (items.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">
            No announcements found.
          </Table.Cell>
        </Table.Row>
      );
    }

    return items.map((announcement) => {
      const active = isAnnouncementActive(announcement);
      const colorMeta = COLOR_MAP[announcement.color] || COLOR_MAP.info;

      return (
        <Table.Row key={announcement._id}>
          <Table.Cell title={announcement.message}>
            {truncateMessage(announcement.message)}
          </Table.Cell>
          <Table.Cell>
            <Label color={colorMeta.color}>{colorMeta.label}</Label>
          </Table.Cell>
          <Table.Cell textAlign="center">
            {announcement.slackEnabled && (
              <Icon name="slack" color="blue" title="Posted to Slack" aria-label="Posted to Slack" />
            )}
          </Table.Cell>
          <Table.Cell>
            <Label color={active ? "green" : "grey"}>
              {active ? "Active" : "Expired"}
            </Label>
          </Table.Cell>
          <Table.Cell>{formatDate(announcement.expiresAt)}</Table.Cell>
          <Table.Cell>{formatDate(announcement.createdAt)}</Table.Cell>
          <Table.Cell>
            <Button
              icon
              size="mini"
              color="blue"
              title="Edit"
              onClick={() => openEditModal(announcement)}
            >
              <Icon name="pencil" />
            </Button>
            {active && (
              <ConfirmPopup
                content="Are you sure you want to expire this announcement now?"
                callback={() => handleExpire(announcement._id)}
              >
                <Button icon size="mini" color="orange" title="Expire Now" disabled={actionLoadingId === announcement._id}>
                  <Icon name="stop" />
                </Button>
              </ConfirmPopup>
            )}
            <ConfirmPopup
              content="Are you sure you want to delete this announcement? This action cannot be undone."
              callback={() => handleDelete(announcement._id)}
            >
              <Button icon size="mini" color="red" title="Delete" disabled={actionLoadingId === announcement._id}>
                <Icon name="trash" />
              </Button>
            </ConfirmPopup>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  return (
    <>
      <Segment.Group>
        <Segment className="tableTopSegment" inverted>
          <Header className="tableTitle" size="large">
            Announcements
            <Button color="teal" onClick={openCreateModal}>
              <Icon name="plus" />
              New Announcement
            </Button>
          </Header>
        </Segment>

        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Message</Table.HeaderCell>
              <Table.HeaderCell>Color</Table.HeaderCell>
              <Table.HeaderCell>Slack</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Expires At</Table.HeaderCell>
              <Table.HeaderCell>Created At</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {renderTableBody()}
          </Table.Body>
          {totalPages > 1 && (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={TABLE_COLUMN_COUNT}>
                  <Pagination
                    floated="right"
                    activePage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
      </Segment.Group>

      <AnnouncementFormModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        announcement={editingAnnouncement}
        loading={modalLoading}
      />
    </>
  );
};

export default AnnouncementsPage;
